import { useEffect, useRef, useState } from 'react';
import type { VideoItem } from '@/mocks/videos';

type PlayerStatus = 'idle' | 'loading' | 'playing' | 'error';

interface DetailPlayerProps {
  item: VideoItem;
}

export default function DetailPlayer({ item }: DetailPlayerProps) {
  const [status, setStatus] = useState<PlayerStatus>('idle');
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    setStatus('idle');
  }, [item.id]);

  const startPlay = () => {
    setStatus('loading');
    timerRef.current = window.setTimeout(() => setStatus('playing'), 2200);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-950">
      <div className="relative aspect-video w-full">
        <img
          src={item.cover}
          alt={item.title}
          title={`${item.title} 播放演示 - 麻豆精品入口蜜桃`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-background-950/45" />

        {status === 'idle' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center">
            <button
              type="button"
              onClick={startPlay}
              aria-label="开始播放演示"
              className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-105"
            >
              <i className="ri-play-fill text-3xl" />
            </button>
            <p className="text-sm text-background-100">点击播放（前端演示，不含真实视频）</p>
          </div>
        )}

        {status === 'loading' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-300" />
            <p className="text-sm text-background-100">正在加载演示视频…</p>
            <div className="h-1 w-56 overflow-hidden rounded-full bg-background-50/20">
              <div className="h-full animate-loading-bar rounded-full bg-primary-400" />
            </div>
          </div>
        )}

        {status === 'playing' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center">
            <span className="flex items-center gap-1.5 rounded-full bg-primary-500/90 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-record-circle-line" />
              演示播放中
            </span>
            <p className="text-base font-medium text-background-50">{item.title}</p>
            <p className="max-w-md px-4 text-xs leading-relaxed text-background-100/80">
              此为前端播放器演示区域，未接入真实视频播放接口，仅展示播放状态与控件样式。
            </p>
            <div className="mt-2 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setStatus('idle')}
                className="flex items-center gap-1 rounded-md bg-background-50/15 px-3 py-1.5 text-xs text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/25"
              >
                <i className="ri-stop-circle-line" />
                结束演示
              </button>
              <button
                type="button"
                onClick={() => setStatus('error')}
                className="flex items-center gap-1 rounded-md bg-background-50/15 px-3 py-1.5 text-xs text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/25"
              >
                <i className="ri-alert-line" />
                模拟播放失败
              </button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background-950/70 text-center">
            <i className="ri-error-warning-line text-3xl text-primary-300" />
            <p className="text-sm font-medium text-background-50">视频暂时无法播放</p>
            <p className="max-w-sm px-4 text-xs leading-relaxed text-background-100/80">
              该内容为演示资源，当前模拟播放失败状态，你可以稍后重试。
            </p>
            <button
              type="button"
              onClick={startPlay}
              className="mt-1 flex items-center gap-1 rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line" />
              重新加载
            </button>
          </div>
        )}
      </div>
    </div>
  );
}